/**
 * W-08a "Lotlar" — the pure logic behind the lot browser and the lot maintenance
 * the owner does on it (G3): which rows a filter chip keeps, what a row's status
 * chip says, and what `update_lot` may be sent.
 *
 * Nothing here knows about a screen. The list reads `v_lot_overview`, which
 * carries no cost column at all (migration 4: "W-08a lot browser (no cost
 * columns)"), so a cost is only ever joined in from `lot_costs` — a table RLS
 * lets owner, partner and accountant read and nobody else.
 */
import { D, type Dec } from '../money';
import { matchesSearch } from '../text';
import { toIsoDate, type DateInput } from '../format';
import { matrixQty } from './matrix';

/** `lot_qc_status` (migration 3). */
export type LotQcStatus = 'none' | 'pending' | 'accepted' | 'passed' | 'failed' | 'quarantine';
/** `lot_origin` (migration 3). */
export type LotOrigin = 'receipt' | 'production' | 'opening' | 'adjustment' | 'return';

/** The columns of `v_lot_overview` the list and the maintenance drawer read. */
export interface LotOverviewInput {
  lot_id?: string | null;
  lot_no?: string | null;
  series?: string | null;
  origin?: LotOrigin | string | null;
  origin_ref_no?: string | null;
  item_id?: string | null;
  sku?: string | null;
  item_name_tr?: string | null;
  short_name?: string | null;
  attributes?: unknown;
  supplier_name?: string | null;
  supplier_lot_no?: string | null;
  received_at?: string | null;
  expiry_date?: string | null;
  qc_status?: LotQcStatus | string | null;
  is_blocked?: boolean | null;
  initial_qty?: number | string | null;
  stock_unit?: string | null;
  stock_unit_factor?: number | null;
  on_hand_qty?: number | string | null;
  locations?: string[] | null;
  last_movement_at?: string | null;
}

/** The chips of §3.1, in the artboard's order. */
export const LOT_FILTERS = ['all', 'inStock', 'depleted', 'quarantine'] as const;
export type LotFilter = (typeof LOT_FILTERS)[number];

/**
 * What the row's own state is, before quality has a say: a blocked lot reads
 * "Karantina" whatever its QC status, because that is the fact that stops it
 * being issued (`fn_post_stock_movement` refuses it).
 */
export type LotState = 'blocked' | 'negative' | 'inStock' | 'depleted';

export function lotState(row: LotOverviewInput): LotState {
  if (row.is_blocked) return 'blocked';
  const onHand = D(row.on_hand_qty ?? 0);
  if (onHand.isNegative()) return 'negative';
  return onHand.isZero() ? 'depleted' : 'inStock';
}

/** On-hand in the item's stock unit — the "Eldeki" column, as W-06 counts it. */
export function lotOnHand(row: LotOverviewInput): Dec {
  return matrixQty(row.on_hand_qty ?? 0, row.stock_unit_factor);
}

/** Initial quantity in the stock unit — the node card's figure and the panel tile. */
export function lotInitialQty(row: LotOverviewInput): Dec {
  return matrixQty(row.initial_qty ?? 0, row.stock_unit_factor);
}

export function matchesLotFilter(row: LotOverviewInput, filter: LotFilter): boolean {
  switch (filter) {
    case 'inStock':
      return !D(row.on_hand_qty ?? 0).isZero();
    case 'depleted':
      return D(row.on_hand_qty ?? 0).isZero();
    case 'quarantine':
      return row.is_blocked === true || row.qc_status === 'quarantine' || row.qc_status === 'failed';
    default:
      return true;
  }
}

/**
 * What the search box matches: the lot number first, then everything a partner
 * would type instead of it — SKU, item name, the supplier's own batch number and
 * the container number the künye shows (§3.1).
 */
export function lotSearchFields(row: LotOverviewInput): (string | null | undefined)[] {
  const attrs = (row.attributes ?? {}) as Record<string, unknown>;
  const container = typeof attrs.container_no === 'string' ? attrs.container_no : null;
  return [row.lot_no, row.sku, row.item_name_tr, row.short_name, row.supplier_lot_no, row.supplier_name, row.origin_ref_no, container];
}

export function matchesLotSearch(row: LotOverviewInput, term: string): boolean {
  return matchesSearch(lotSearchFields(row), term);
}

/**
 * The "Kaynak" cell. A lot that came from a document shows the document number;
 * one the opening import created has none, and its origin is the label instead
 * ("Açılış stoğu"), so the column never falls back to an empty cell.
 */
export function lotSourceKey(row: LotOverviewInput): { docNo: string | null; originKey: string } {
  const origin = typeof row.origin === 'string' ? row.origin : 'adjustment';
  return { docNo: row.origin_ref_no?.trim() || null, originKey: origin };
}

/**
 * Whether the expiry date has already passed. Only the passed case is coloured:
 * "expiring soon" would need a window nobody has confirmed, and nothing Aslen
 * stocks is shelf-life tracked yet (E-row, MISSING_INPUTS).
 */
export function isLotExpired(expiry: string | null | undefined, now: DateInput = new Date()): boolean {
  return !!expiry && expiry < toIsoDate(now);
}

/** "D1-A-02 +2" — the first location code and how many more hold the lot. */
export function lotLocationLabel(locations: string[] | null | undefined): { first: string | null; more: number } {
  const codes = (locations ?? []).filter(Boolean).slice().sort((a, b) => a.localeCompare(b, 'tr'));
  return { first: codes[0] ?? null, more: Math.max(codes.length - 1, 0) };
}

// ── maintenance (G3) ────────────────────────────────────────────────────────
/**
 * What the drawer may change. Quantities are absent by design: a lot's quantity
 * is the sum of its movements and only the posting engine may move it, which is
 * why `update_lot` has no quantity argument either.
 */
export interface LotPatchInput {
  expiryDate?: string | null;
  supplierLotNo?: string | null;
}

export type LotPatchError = { field: 'expiryDate'; hint: 'invalid' | 'beforeReceipt' };

export type LotPatchResult = { ok: true; patch: Record<string, string | null>; changed: boolean } | ({ ok: false } & LotPatchError);

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Builds the `update_lot` patch, with only the keys that actually change — the
 * RPC applies a key when it is present (`p_patch ? 'expiry_date'`), so sending
 * the whole form would stamp `updated_by` on fields nobody touched and make the
 * audit log lie about what happened.
 */
export function buildLotPatch(input: LotPatchInput, row: LotOverviewInput): LotPatchResult {
  const patch: Record<string, string | null> = {};

  if (input.expiryDate !== undefined) {
    const next = input.expiryDate?.trim() || null;
    if (next !== null && !ISO_DATE.test(next)) return { ok: false, field: 'expiryDate', hint: 'invalid' };
    // an expiry before the goods arrived is a typo, not a short shelf life
    if (next !== null && row.received_at && next < toIsoDate(row.received_at)) return { ok: false, field: 'expiryDate', hint: 'beforeReceipt' };
    if (next !== (row.expiry_date ?? null)) patch.expiry_date = next;
  }

  if (input.supplierLotNo !== undefined) {
    const next = input.supplierLotNo?.trim() || null;
    if (next !== (row.supplier_lot_no ?? null)) patch.supplier_lot_no = next;
  }

  return { ok: true, patch, changed: Object.keys(patch).length > 0 };
}

/** The block / unblock write: one boolean, so the dialog cannot send anything else. */
export function lotBlockPatch(blocked: boolean): Record<string, boolean> {
  return { is_blocked: blocked };
}
